/**
 * Pricing for the SMB self-serve tier.
 *
 * The rate card here is the decided one (Rate Card Rev 0.5, ruled by Nachi
 * 2026-09-16): a fixed per-hire rate keyed to the role band, i.e. to what the
 * role pays. Hiring volume sets the contract value (hires x rate). It does
 * not set the rate. An earlier version of this file used a $300-500/hire
 * volume discount from the internal proposal. That model was not the one
 * that was decided.
 *
 * KEEP IN SYNC BY HAND with avrio-backend's
 * `service/signup/SelfServeRateCard.java`. There is no shared schema between
 * the two repos. Once the backend computes pricing server-side
 * (avrio-backend#167), this copy only sets the quote shown before
 * submission. Until then, the figures computed here are what the signup
 * request sends.
 *
 * Kept as pure functions with no React and no network so the money maths can be
 * tested directly. Everything here is derived from the prospect's own answers;
 * nothing is fetched and nothing is persisted.
 */

export type RoleType = 'hourly' | 'trades' | 'office' | 'leadership';

/**
 * Dollars per hire, by role band. Executive / C-suite search is quoted
 * individually from $100,000 per search and is not a band here: `leadership`
 * is the top per-hire band for VP-and-above roles hired through this flow.
 */
export const ROLE_BAND_RATES: Readonly<Record<RoleType, number>> = {
  hourly: 1000,
  trades: 3000,
  office: 5000,
  leadership: 25000,
};

/**
 * Industry cost-per-hire benchmark used for the comparison line.
 *
 * One number standing in for every industry, role type and seniority, so the
 * comparison it produces is indicative and the UI says so. It is not a claim
 * about this particular prospect's current spend -- when they tell us their
 * actual spend we compare against that instead, which is the honest version.
 */
export const BENCHMARK_COST_PER_HIRE = 5475;

/** Share of contract value taken up front. */
export const DOWN_PAYMENT_SHARE = 0.25;

/** Rate Card Rev 0.5 was signed off 2026-09-16. Flipping this changes wording, not maths. */
export const RATE_CARD_SIGNED_OFF = true;

export type Volatility = 'steady' | 'bursty';
export type CurrentTool = 'inhouse' | 'linkedin' | 'agency' | 'mix';

export interface QualificationAnswers {
  hires: number;
  volatility: Volatility;
  roleType: RoleType;
  tool: CurrentTool;
  /** null when the prospect skipped it, which is offered and expected. */
  annualSpend: number | null;
}

export interface Quote {
  hires: number;
  ratePerHire: number;
  contractValue: number;
  downPayment: number;
  drawDown: number;
  /** Absent when we have no benchmark to compare against. */
  benchmarkTotal: number;
  benchmarkSaving: number;
  benchmarkSavingPct: number;
  /** Only present when the prospect gave us a spend figure. */
  ownSpendDifference: number | null;
}

/**
 * The hiring volumes self-serve will quote, matching SelfServeRateCard's
 * MIN_HIRES/MAX_HIRES. Above the ceiling the server refuses the signup, so
 * the flow has to stop the prospect here rather than at a failed payment step.
 */
export const MIN_SELF_SERVE_HIRES = 1;
export const MAX_SELF_SERVE_HIRES = 1000;

/** Dollars per hire for a role band. */
export const rateForRoleBand = (roleType: RoleType): number =>
  ROLE_BAND_RATES[roleType];

/**
 * Turn the five answers into the numbers shown on the summary card.
 *
 * Rounds only at the point of display-facing values, so downPayment + drawDown
 * always equals contractValue exactly. Splitting a contract into two figures
 * that do not add up to it is the kind of arithmetic nobody forgives.
 */
export const buildQuote = (answers: QualificationAnswers): Quote => {
  const hires = Math.max(0, Math.round(answers.hires));
  const ratePerHire = rateForRoleBand(answers.roleType);
  const contractValue = hires * ratePerHire;
  const downPayment = Math.round(contractValue * DOWN_PAYMENT_SHARE);
  const drawDown = contractValue - downPayment;

  const benchmarkTotal = hires * BENCHMARK_COST_PER_HIRE;
  const benchmarkSaving = benchmarkTotal - contractValue;
  const benchmarkSavingPct =
    benchmarkTotal > 0
      ? Math.round((benchmarkSaving / benchmarkTotal) * 100)
      : 0;

  return {
    hires,
    ratePerHire,
    contractValue,
    downPayment,
    drawDown,
    benchmarkTotal,
    benchmarkSaving,
    benchmarkSavingPct,
    ownSpendDifference:
      answers.annualSpend === null ? null : answers.annualSpend - contractValue,
  };
};

/** US dollars, no cents. Money in this flow is never fractional. */
export const formatUsd = (n: number): string =>
  `$${Math.round(n).toLocaleString('en-US')}`;

/**
 * Pull a hiring volume out of free text.
 *
 * Returns null rather than guessing, so the caller can ask again instead of
 * quoting a contract off a number nobody typed. "about 120 next year" -> 120;
 * "not sure" -> null.
 */
export const parseCount = (text: string): number | null => {
  const match = text.replace(/,/g, '').match(/\d+(\.\d+)?/);
  if (!match) return null;
  const n = Math.round(parseFloat(match[0]));
  return Number.isFinite(n) && n > 0 ? n : null;
};
